import React, { useCallback, useMemo, useRef, useState } from "react";
import { Loader2, RefreshCcw } from "lucide-react";

import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { AccountSettingsFieldSelect } from "@/views/Dashboard/Profile/components/AccountSettingsFieldSelect";
import SettingCard, {
  accountSettingsCardStackClassName,
  accountSettingsFieldClassName,
} from "@/views/Dashboard/Profile/components/SettingCard";
import type {
  InstrumentSetupResolvedRow,
  InstrumentSetupResponse,
  InstrumentSetupRow,
  MarketDataPreferenceSkipReason,
} from "../../types/instrumentSetup";
import { instrumentSetupRowFromResolvedSummary, formatInstrumentSetupListingSummary } from "../../utils/instrumentSetupManageRow";
import {
  formatInstrumentSetupProgressDescription,
  instrumentSetupRowNeedsConfirmation,
  setupProgress,
} from "../../utils/instrumentMarketDataReadiness";
import {
  needsInstrumentSetupInitialConfirm,
  type InstrumentRowEditorSelection,
} from "../../utils/instrumentSetupSave";
import { InstrumentMarketDataRowEditor } from "./InstrumentMarketDataRowEditor";
import {
  InstrumentSetupSelectPortalProvider,
} from "./InstrumentSetupSelectPortal";

function isInstrumentSetupPortaledControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest("[data-account-settings-field-select-content]") ||
      target.closest('[data-slot="select-content"]') ||
      target.closest('[data-slot="select-trigger"]') ||
      target.closest("[data-market-data-broker-reconnect-dialog]")
  );
}

type WizardListItem =
  | { kind: "resolved"; row: InstrumentSetupResolvedRow }
  | { kind: "pending"; row: InstrumentSetupRow; pendingIndex: number };

function formatListingSummary(row: InstrumentSetupResolvedRow | InstrumentSetupRow): string {
  return formatInstrumentSetupListingSummary(row);
}

export function InstrumentSetupWizard(props: {
  isOpen: boolean;
  mandatory: boolean;
  loading: boolean;
  saving: boolean;
  setupData: InstrumentSetupResponse | null;
  focusSymbol: string | null;
  pendingRows: InstrumentSetupRow[];
  resolvedRows: InstrumentSetupResolvedRow[];
  onSaveRow: (
    row: InstrumentSetupRow,
    selection: InstrumentRowEditorSelection,
    consentGranted: boolean,
    resolveContext?: { md_listing_key?: string; instrument_id?: string; asset_class?: string }
  ) => Promise<boolean>;
  onSkipRow: (
    row: InstrumentSetupRow,
    reason?: MarketDataPreferenceSkipReason
  ) => Promise<boolean>;
  onRefreshScan: () => Promise<unknown>;
  onDismiss: () => void;
}): React.ReactElement {
  const listItems = useMemo<WizardListItem[]>(() => {
    const resolved = props.resolvedRows.map((row) => ({ kind: "resolved" as const, row }));
    const pending = props.pendingRows.map((row, pendingIndex) => ({
      kind: "pending" as const,
      row,
      pendingIndex,
    }));
    return [...resolved, ...pending];
  }, [props.pendingRows, props.resolvedRows]);

  const initialSymbol = useMemo(() => {
    if (props.focusSymbol?.trim() && listItems.length > 0) {
      const match = listItems.find((item) => item.row.symbol === props.focusSymbol?.trim());
      if (match) return match.row.symbol;
    }
    const firstPending = listItems.find((item) => item.kind === "pending");
    return firstPending?.row.symbol ?? listItems[0]?.row.symbol ?? null;
  }, [props.focusSymbol, listItems]);

  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(initialSymbol);
  const [lastSavedSymbol, setLastSavedSymbol] = useState<string | null>(null);
  const [selectPortalContainer, setSelectPortalContainer] = useState<HTMLElement | null>(null);
  const nestedDialogOpenRef = useRef(false);
  const selectMenuOpenRef = useRef(false);

  const handleNestedDialogOpenChange = useCallback((open: boolean) => {
    nestedDialogOpenRef.current = open;
  }, []);

  const handleSelectMenuOpenChange = useCallback((open: boolean) => {
    selectMenuOpenRef.current = open;
  }, []);

  const handleDismissRequest = useCallback(() => {
    if (nestedDialogOpenRef.current || selectMenuOpenRef.current) return;
    props.onDismiss();
  }, [props.onDismiss]);

  React.useEffect(() => {
    if (!props.isOpen) return;
    setSelectedSymbol(initialSymbol);
    nestedDialogOpenRef.current = false;
  }, [props.isOpen, initialSymbol]);

  React.useEffect(() => {
    if (!lastSavedSymbol) return;
    const firstPending = listItems.find((item) => item.kind === "pending");
    setSelectedSymbol(firstPending?.row.symbol ?? listItems[0]?.row.symbol ?? null);
    setLastSavedSymbol(null);
  }, [listItems, lastSavedSymbol]);

  const symbolSelectOptions = useMemo(
    () =>
      listItems.map((item) => {
        const symbol = item.row.symbol;
        const needsConfirmation = instrumentSetupRowNeedsConfirmation(item.row);
        if (needsConfirmation) {
          return {
            value: symbol,
            label: `${symbol} · Needs confirmation`,
          };
        }
        return {
          value: symbol,
          label: `${symbol} · ${formatListingSummary(item.row)}`,
        };
      }),
    [listItems]
  );

  const { needsConfirmation: pendingCount } = setupProgress(props.setupData);
  const activeItem =
    listItems.find((item) => item.row.symbol === selectedSymbol) ?? listItems[0] ?? null;
  const accountCode = props.setupData?.broker_account_code ?? "";

  const editorRow = useMemo(() => {
    if (!activeItem) return null;
    if (activeItem.kind === "pending") return activeItem.row;
    return instrumentSetupRowFromResolvedSummary(activeItem.row);
  }, [activeItem]);

  const progressDescription = formatInstrumentSetupProgressDescription(props.setupData);

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(open) => {
        if (!open) handleDismissRequest();
      }}
    >
      <InstrumentSetupSelectPortalProvider
        container={selectPortalContainer}
        onSelectOpenChange={handleSelectMenuOpenChange}
      >
        <DialogContent
          ref={setSelectPortalContainer}
          data-instrument-setup-wizard=""
          stackLayer="elevated"
          showCloseButton={false}
          className="flex max-h-[90vh] flex-col gap-0 overflow-visible p-0 font-[family-name:var(--font-primary)] sm:max-w-3xl"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            if (
              nestedDialogOpenRef.current ||
              selectMenuOpenRef.current ||
              isInstrumentSetupPortaledControl(event.target)
            ) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            if (
              nestedDialogOpenRef.current ||
              selectMenuOpenRef.current ||
              isInstrumentSetupPortaledControl(event.target)
            ) {
              event.preventDefault();
            }
          }}
        >
        <DialogHeader className="shrink-0 space-y-2 border-b px-6 py-4 pr-14">
          <DialogTitle className="text-[color:var(--brand-tropical-surf)]">
            Confirm market data for your traded symbols
          </DialogTitle>
          <DialogDescription className={cn(BROKER_FIELD_DESC_CLASS, "m-0")}>
            {props.mandatory
              ? "Review auto-configured symbols and confirm OHLC sources for any that still need your choice before using Trade Review, Exit Placement, Entry Analysis, or the Simulator."
              : "Confirm how OHLC should be loaded for symbols on this account."}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 py-4">
          {props.loading ? (
            <div
              className={cn(
                BROKER_FIELD_DESC_CLASS,
                "flex min-h-[12rem] items-center justify-center gap-2"
              )}
            >
              <Loader2 className="size-5 animate-spin text-[color:var(--brand-tropical-surf)]" />
              Loading traded symbols…
            </div>
          ) : listItems.length === 0 ? (
            <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0 text-sm")}>
              {props.mandatory
                ? "No symbols were returned yet. Use Refresh sources to load your traded symbols."
                : "All symbols are configured."}
            </p>
          ) : (
            <div className={accountSettingsCardStackClassName} data-account-settings-card-stack="">
              <SettingCard
                title="Symbols"
                description={progressDescription}
                rightContent={
                  <Button
                    type="button"
                    variant="outline-gray"
                    size="sm"
                    data-font="primary"
                    className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
                    disabled={props.loading}
                    onClick={() => void props.onRefreshScan()}
                  >
                    {props.loading ? (
                      <Loader2 className="size-4 animate-spin" aria-hidden />
                    ) : (
                      <RefreshCcw className="size-4" aria-hidden />
                    )}
                    Refresh sources
                  </Button>
                }
              >
                <div className="flex w-full min-w-0 flex-col gap-4">
                  <Field className={cn(accountSettingsFieldClassName, "w-full max-w-md")}>
                    <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Symbol</FieldLabel>
                    <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                      Pick a traded symbol to review or change its OHLC market data source.
                    </FieldDescription>
                    <AccountSettingsFieldSelect
                      id="instrument-setup-wizard-symbol"
                      value={selectedSymbol ?? ""}
                      onValueChange={(value) => setSelectedSymbol(value || null)}
                      placeholder="Select symbol"
                      options={symbolSelectOptions}
                    />
                  </Field>

                  {editorRow && accountCode ? (
                    <InstrumentMarketDataRowEditor
                      key={`${accountCode}:${selectedSymbol ?? editorRow.symbol}`}
                      row={editorRow}
                      brokerAccountCode={accountCode}
                      assetClassHints={props.setupData?.broker_asset_class_hints}
                      alwaysShowSourcePicker={activeItem?.kind === "resolved"}
                      requireDirtyToSave={
                        activeItem?.kind === "resolved" &&
                        editorRow != null &&
                        !needsInstrumentSetupInitialConfirm(editorRow)
                      }
                      saving={props.saving}
                      compactHeader
                      onNestedDialogOpenChange={handleNestedDialogOpenChange}
                      onSkip={
                        activeItem?.kind === "pending"
                          ? async (reason) => {
                              const ok = await props.onSkipRow(activeItem.row, reason);
                              if (ok) {
                                setLastSavedSymbol(activeItem.row.symbol);
                              }
                            }
                          : undefined
                      }
                      onSave={async (selection, consentGranted, resolveContext) => {
                        if (!activeItem) return false;
                        const row =
                          activeItem.kind === "pending" ? activeItem.row : editorRow;
                        const ok = await props.onSaveRow(
                          row,
                          selection,
                          consentGranted,
                          resolveContext
                        );
                        if (ok && activeItem.kind === "pending") {
                          setLastSavedSymbol(activeItem.row.symbol);
                        }
                        return ok;
                      }}
                      onSetupRefresh={async () => {
                        await props.onRefreshScan();
                      }}
                    />
                  ) : (
                    <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0")}>
                      Select a symbol to view or change its market data source.
                    </p>
                  )}
                </div>
              </SettingCard>
            </div>
          )}
        </div>

        <DialogFooter
          data-instrument-setup-wizard-footer=""
          showCloseButton={false}
          className="mx-0 mb-0 mt-0 w-full shrink-0 justify-end gap-3 rounded-b-[6px] border-t border-border bg-muted px-6 py-4 sm:flex-row sm:justify-end"
        >
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={handleDismissRequest}
          >
            <span data-font="primary" className="truncate normal-case tracking-normal">
              {props.mandatory && pendingCount > 0 ? "Continue later" : "Done"}
            </span>
          </Button>
        </DialogFooter>
        </DialogContent>
      </InstrumentSetupSelectPortalProvider>
    </Dialog>
  );
}
